import type {
    AnchorHTMLAttributes,
    ButtonHTMLAttributes,
    ForwardedRef,
    HTMLAttributes,
    LiHTMLAttributes,
    ReactNode,
} from "react";
import { forwardRef } from "react";
import { cx } from "../../utils/cx";
import styles from "./Navigation.module.scss";

export type NavigationProps = Omit<HTMLAttributes<HTMLElement>, "as"> & {
    /**
     * Narrow icon rail (default **54px**). Pair with `expandOnHover` for
     * hover/focus expand to **320px**. Override with `--nav-width-collapsed` /
     * `--nav-width-expanded` on this element. Elevation shadow: `--nav-elevation-shadow`.
     */
    rail?: boolean;
    /** When `rail`, width grows on hover / focus-within. */
    expandOnHover?: boolean;
    /** Host element. Default `aside`. */
    as?: "aside" | "nav";
};

function NavigationRoot({
    as: Tag = "aside",
    rail = false,
    expandOnHover = false,
    className,
    children,
    ...rest
}: NavigationProps) {
    return (
        <Tag
            className={cx(styles.root, className)}
            data-expand-hover={expandOnHover && rail ? "true" : "false"}
            data-rail={rail ? "true" : "false"}
            data-ui="navigation"
            {...rest}
        >
            {children}
        </Tag>
    );
}

NavigationRoot.displayName = "Navigation";

export function NavigationHeader({
    className,
    children,
    ...rest
}: HTMLAttributes<HTMLDivElement>) {
    return (
        <div className={cx(styles.header, className)} {...rest}>
            {children}
        </div>
    );
}

NavigationHeader.displayName = "Navigation.Header";

export function NavigationFooter({
    className,
    children,
    ...rest
}: HTMLAttributes<HTMLDivElement>) {
    return (
        <div className={cx(styles.footer, className)} {...rest}>
            {children}
        </div>
    );
}

NavigationFooter.displayName = "Navigation.Footer";

export type NavigationListProps = HTMLAttributes<HTMLUListElement> & {
    /**
     * `fill` grows the list in the flex column (default). Use `anchor` below a
     * `Navigation.Spacer` so primary links stay at the bottom (Figma layout).
     */
    listBehavior?: "fill" | "anchor";
};

export function NavigationList({
    className,
    children,
    listBehavior = "fill",
    ...rest
}: NavigationListProps) {
    return (
        <ul
            className={cx(
                styles.list,
                listBehavior === "anchor" && styles.listAnchor,
                className,
            )}
            {...rest}
        >
            {children}
        </ul>
    );
}

NavigationList.displayName = "Navigation.List";

export type NavigationContextTriggerProps = Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    "title"
> & {
    /** Small grey label above `heading` (e.g. Organization). Omit for single-line rows (e.g. Retail Analytics) — icon renders without the bordered tile. */
    metaLabel?: string;
    /** Primary line: org/store name, or the only line when `metaLabel` is omitted. */
    heading: ReactNode;
    icon?: ReactNode;
    /** When set, replaces the default trailing control (chevron). Pass `null` for no trailing. */
    trailing?: ReactNode | null;
    /**
     * When `true`, shows the default chevron. When `false`, hides it.
     * Default: `true` if `metaLabel` is set, otherwise `false`.
     */
    chevron?: boolean;
};

export const NavigationContextTrigger = forwardRef<
    HTMLButtonElement,
    NavigationContextTriggerProps
>(function NavigationContextTrigger(
    {
        metaLabel,
        heading,
        icon,
        trailing,
        chevron,
        className,
        type = "button",
        ...rest
    },
    ref: ForwardedRef<HTMLButtonElement>,
) {
    const showChevron = chevron ?? Boolean(metaLabel);
    const trailingNode =
        trailing !== undefined ? (
            trailing
        ) : showChevron ? (
            <span aria-hidden className={styles.contextChevron}>
                <ChevronPair />
            </span>
        ) : null;

    return (
        <button
            className={cx(styles.contextTrigger, className)}
            ref={ref}
            type={type}
            {...rest}
        >
            {icon ? (
                <span
                    className={cx(
                        styles.contextIcon,
                        !metaLabel && styles.contextIconPlain,
                    )}
                >
                    {icon}
                </span>
            ) : null}
            <span
                className={styles.contextBody}
                data-simple={metaLabel ? undefined : "true"}
            >
                {metaLabel ? (
                    <span className={styles.contextMeta}>{metaLabel}</span>
                ) : null}
                <span className={styles.contextTitle}>{heading}</span>
            </span>
            {trailingNode}
        </button>
    );
});

NavigationContextTrigger.displayName = "Navigation.ContextTrigger";

function ChevronPair() {
    return (
        <svg fill="none" height={18} viewBox="0 0 18 18" width={18}>
            <path
                d="M5 7l4 4 4-4M5 11l4 4 4-4"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.25"
            />
        </svg>
    );
}

export type NavigationItemTrailingStatus = "hint" | "success" | "warning";

export type NavigationItemProps = {
    icon?: ReactNode;
    active?: boolean;
    /** Full-nested row with trailing status (Figma nested). */
    nested?: boolean;
    trailing?: ReactNode;
    trailingStatus?: NavigationItemTrailingStatus;
    children: ReactNode;
    className?: string;
    disabled?: boolean;
    /** When set, renders `<a>`; otherwise `<button type="button">`. */
    href?: string;
} & Omit<
    AnchorHTMLAttributes<HTMLAnchorElement>,
    "children" | "className" | "href"
> &
    Omit<
        ButtonHTMLAttributes<HTMLButtonElement>,
        "children" | "className" | "href"
    >;

export const NavigationItem = forwardRef<
    HTMLAnchorElement | HTMLButtonElement,
    NavigationItemProps
>(function NavigationItem(props, ref) {
    const {
        href,
        icon,
        active,
        nested,
        trailing,
        trailingStatus = "hint",
        children,
        className,
        disabled,
        type = "button",
        ...rest
    } = props;

    const content = nested ? (
        <>
            <span className={styles.itemMain}>
                {icon ? <span className={styles.itemIcon}>{icon}</span> : null}
                <span className={styles.itemLabel}>{children}</span>
            </span>
            {trailing ? (
                <span
                    className={styles.itemTrailing}
                    data-status={trailingStatus}
                >
                    {trailing}
                </span>
            ) : null}
        </>
    ) : (
        <>
            {icon ? <span className={styles.itemIcon}>{icon}</span> : null}
            <span className={styles.itemLabel}>{children}</span>
        </>
    );

    const shared = {
        "aria-current": active && href ? ("page" as const) : undefined,
        className: cx(styles.item, className),
        "data-active": active ? "true" : undefined,
        "data-nested": nested ? "true" : undefined,
        "data-ui": "navigation-item" as const,
    };

    if (disabled) {
        return (
            <span
                aria-disabled="true"
                className={cx(styles.item, className)}
                data-active={active ? "true" : undefined}
                data-nested={nested ? "true" : undefined}
                data-ui="navigation-item"
                role="presentation"
            >
                {content}
            </span>
        );
    }

    if (href != null && href !== "") {
        return (
            <a
                href={href}
                ref={ref as ForwardedRef<HTMLAnchorElement>}
                {...shared}
                {...(rest as AnchorHTMLAttributes<HTMLAnchorElement>)}
            >
                {content}
            </a>
        );
    }

    return (
        <button
            ref={ref as ForwardedRef<HTMLButtonElement>}
            type={type}
            {...shared}
            {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)}
        >
            {content}
        </button>
    );
});

NavigationItem.displayName = "NavigationItem";

export type NavigationListItemProps = LiHTMLAttributes<HTMLLIElement>;

export function NavigationListItem({
    className,
    children,
    ...rest
}: NavigationListItemProps) {
    return (
        <li className={cx(styles.listItem, className)} {...rest}>
            {children}
        </li>
    );
}

NavigationListItem.displayName = "Navigation.ListItem";

export function NavigationSpacer(props: HTMLAttributes<HTMLDivElement>) {
    const { className, ...rest } = props;
    return (
        <div aria-hidden className={cx(styles.spacer, className)} {...rest} />
    );
}

NavigationSpacer.displayName = "Navigation.Spacer";

export const Navigation = Object.assign(NavigationRoot, {
    Header: NavigationHeader,
    Footer: NavigationFooter,
    List: NavigationList,
    ListItem: NavigationListItem,
    Spacer: NavigationSpacer,
    ContextTrigger: NavigationContextTrigger,
});
